import React from "react";
import { createPortal } from "react-dom";
import CloseIcon from "@mui/icons-material/Close";
import "../../styles/popup.scss";
import "../../styles/common/AiEvalPopup.scss";

const AiEvalBreakdownPopup = ({ open, onClose, evalData }) => {
  if (!open) return null;

  // Robustly parse evalData to extract overall score and individual breakdown scores
  const parseEvalData = (raw) => {
    if (!raw) return { overall: null, scores: {} };
    
    let record = raw;
    if (typeof record === "string") {
      try {
        record = JSON.parse(record);
      } catch (e) {
        try {
          const validJson = record.replace(/'/g, '"');
          record = JSON.parse(validJson);
        } catch (e2) {
          return { overall: null, scores: {} };
        }
      }
    }

    // Handle wrap from endpoint: { enabled: true, is_evaluated: true, data: { ... } }
    if (record && record.data) {
      if (typeof record.data === "string") {
        try {
          record = JSON.parse(record.data);
        } catch (e) {
          try {
            const validJson = record.data.replace(/'/g, '"');
            record = JSON.parse(validJson);
          } catch (e2) {
            // ignore
          }
        }
      } else {
        record = record.data;
      }
    }

    if (!record || typeof record !== "object") {
      return { overall: null, scores: {} };
    }

    // Extract overall score
    let overall = record.overall_score ?? record.overallScore ?? null;
    if (overall !== null) {
      const parsedNum = parseFloat(overall);
      if (!isNaN(parsedNum)) {
        overall = parsedNum;
      } else {
        overall = null;
      }
    }

    // Extract evaluation score (breakdown)
    let scores = record.evalution_score ?? record.evalutionScore ?? record.evaluation_score ?? record.evaluationScore ?? {};
    if (typeof scores === "string") {
      try {
        scores = JSON.parse(scores);
      } catch (e) {
        try {
          // python str(dict) representation uses single quotes; convert to valid JSON double quotes
          const validJson = scores.replace(/'/g, '"');
          scores = JSON.parse(validJson);
        } catch (e2) {
          scores = {};
        }
      }
    }

    return {
      overall,
      scores: typeof scores === "object" && scores !== null ? scores : {},
    };
  };

  const { overall, scores } = parseEvalData(evalData);

  // Check if there is any meaningful evaluation data at all
  const hasNoData = overall === null && Object.keys(scores || {}).length === 0;

  const formatLabel = (raw) => {
    if (!raw) return "";
    return String(raw)
      .replace(/([A-Z])/g, " $1")
      .replace(/_/g, " ")
      .split(" ")
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(" ");
  };

  const toPct = (val) => {
    const num = Number(val);
    if (!Number.isFinite(num)) return 0;
    return Math.max(0, Math.min(100, (num / 10) * 100));
  };

  const toScoreOutOfTen = (val) => {
    const num = Number(val);
    if (!Number.isFinite(num)) return null;
    return Math.max(0, Math.min(10, num));
  };

  const formatOutOfTen = (val) => {
    const score = toScoreOutOfTen(val);
    if (score === null) return "N/A";
    const text = Number.isInteger(score) ? String(score) : score.toFixed(1);
    return `${text}/10`;
  };

  const getBarColor = (val) => {
    const score = toScoreOutOfTen(val);
    if (score === null) return "var(--color-gray, #9097a6)";
    if (score <= 3) return "var(--color-danger, #ef4444)";
    if (score <= 7) return "var(--color-orange, #fca43b)";
    return "var(--color-success, #10b981)";
  };

  // Filter numeric breakdown metrics
  const items = Object.entries(scores || {})
    .filter(([key, value]) => {
      const lowerKey = key.toLowerCase();
      if (
        lowerKey === "overall_score" ||
        lowerKey === "overallscore" ||
        lowerKey === "overall" ||
        lowerKey === "recommendations" ||
        lowerKey === "recommendation" ||
        lowerKey === "improvement_advice" ||
        lowerKey === "feedback" ||
        lowerKey === "content"
      ) {
        return false;
      }
      const num = Number(value);
      return Number.isFinite(num);
    })
    .map(([key, value]) => ({ key, value }));

  // Calculate percentage out of 100
  let percentage = 0;
  if (overall !== null) {
    if (overall <= 10) {
      percentage = Math.round(overall * 10);
    } else {
      percentage = Math.round(overall);
    }
  }

  return createPortal(
    <div
      className="common-popup-overlay high-z-index eval-score-details-popup"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="common-popup-container eval-score-details-popup-container"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="popup-header with-close">
          <div className="popup-title-box">
            <h2 className="popup-title">Eval Score</h2>
            {!hasNoData && (
              <p className="popup-subtitle">
                {overall !== null
                  ? `Overall Score: ${formatOutOfTen(overall)} (${percentage}%)`
                  : `You scored ${percentage}% in this assessment.`}
              </p>
            )}
          </div>
          <button
            type="button"
            className="icon-button popup-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div className="popup-content eval-score-details-content">
          {hasNoData ? (
            <div className="eval-empty-state">
              <div className="eval-empty-state__icon">📊</div>
              <h3 className="eval-empty-state__title">No Evaluation Found</h3>
              <p className="eval-empty-state__msg">
                No evaluation data is available yet. Complete the content and submit to receive your AI evaluation.
              </p>
            </div>
          ) : items.length > 0 ? (
            <div style={{ marginTop: "8px" }}>
              <div className="eval-recommendations-section">
                <div className="section-title" style={{ marginBottom: "16px" }}>EVALUATION BREAKDOWN:</div>
              </div>
              <div className="eval-score-breakdown">
                {items.map((it) => (
                  <div key={it.key} className="eval-score-row">
                    <div className="eval-score-row__label">{formatLabel(it.key)}</div>
                    <div className="eval-score-row__bar">
                      <div
                        className="eval-score-row__barFill"
                        style={{
                          width: `${toPct(it.value)}%`,
                          background: getBarColor(it.value),
                        }}
                      />
                    </div>
                    <div className="eval-score-row__value">{formatOutOfTen(it.value)}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="eval-empty-state">
              <div className="eval-empty-state__icon">📊</div>
              <h3 className="eval-empty-state__title">No Evaluation Found</h3>
              <p className="eval-empty-state__msg">
                No evaluation metrics are available for this content yet.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AiEvalBreakdownPopup;
